import { createServerFn } from "@tanstack/react-start";
import { v4 as uuidv4 } from "uuid";

import { authMiddleware } from "~/lib/auth-middleware";
import { requireClerkCoachAuth } from "~/lib/coach-middleware";
import { S3Middleware } from "~/lib/s3-middleware";
import {
  CreateBucketListItemSchema,
  DeleteBucketListItemSchema,
  GetBucketItemUploadUrlsSchema,
  GetSharedBucketListSchema,
  GetUpdateBucketItemUploadUrlsSchema,
  MoveBucketListItemSchema,
  UpdateBucketListItemSchema,
} from "~/lib/schemas/bucket-list";

export const getBucketItemUploadUrlsFn = createServerFn({ method: "POST" })
  .middleware([S3Middleware, authMiddleware])
  .validator(GetBucketItemUploadUrlsSchema)
  .handler(async ({ data, context }) => {
    const { buildBucketListItemKey, getPresignedUploadUrl } =
      await import("~/lib/server/s3");
    const userId = context.user.id;

    if (!data.files || data.files.length === 0) {
      return { uploads: [] };
    }

    const results = await Promise.all(
      data.files.map(
        async (file: {
          tempId: string;
          filename: string;
          contentType: string;
        }) => {
          const imageId = uuidv4();
          const ext = file.filename.split(".").pop() || "bin";

          const key = buildBucketListItemKey({
            userId,
            itemId: data.id,
            imageId,
            ext,
          });

          const signed = await getPresignedUploadUrl({
            key,
            contentType: file.contentType,
          });

          return {
            tempId: file.tempId,
            uploadUrl: signed.url,
            s3Key: key,
          };
        },
      ),
    );

    return { uploads: results };
  });

export const createBucketListItemFn = createServerFn({ method: "POST" })
  .middleware([authMiddleware])
  .validator(CreateBucketListItemSchema)
  .handler(async ({ data, context }) => {
    const { createBucketListItem } = await import("~/lib/server/bucket-lists");
    const { keyGrants, ...item } = data;
    return createBucketListItem(context.user.id, item, keyGrants);
  });

export const getBucketListFn = createServerFn({ method: "GET" })
  .middleware([S3Middleware, authMiddleware])
  .handler(async ({ context }) => {
    const { getBucketList } = await import("~/lib/server/bucket-lists");
    const targetUserId = context.user.id;

    return getBucketList(targetUserId);
  });

export const deleteBucketListItemFn = createServerFn({ method: "POST" })
  .middleware([authMiddleware])
  .validator(DeleteBucketListItemSchema)
  .handler(async ({ data, context }) => {
    const { deleteBucketList } = await import("~/lib/server/bucket-lists");
    const userId = context.user.id;
    const itemId = data.id;

    return deleteBucketList(itemId, userId);
  });

export const getUpdateBucketItemUploadUrlsFn = createServerFn({
  method: "POST",
})
  .middleware([S3Middleware, authMiddleware])
  .validator(GetUpdateBucketItemUploadUrlsSchema)
  .handler(async ({ data, context }) => {
    const { buildBucketListItemKey, getPresignedUploadUrl } =
      await import("~/lib/server/s3");
    const itemId = data.id;
    const userId = context.user.id;

    if (!data.images || data.images.length === 0) {
      return { uploads: [] };
    }

    const results = await Promise.all(
      data.images.map(
        async (file: {
          tempId: string;
          filename: string;
          contentType: string;
        }) => {
          const imageId = uuidv4();
          const ext = file.filename.split(".").pop() || "bin";

          const key = buildBucketListItemKey({ userId, itemId, imageId, ext });

          const signed = await getPresignedUploadUrl({
            key,
            contentType: file.contentType,
          });

          return {
            tempId: file.tempId,
            uploadUrl: signed.url,
            s3Key: key,
          };
        },
      ),
    );

    return { uploads: results };
  });

export const updateBucketListItemFn = createServerFn({ method: "POST" })
  .middleware([authMiddleware])
  .validator(UpdateBucketListItemSchema)
  .handler(async ({ data, context }) => {
    const { updateBucketListItem } = await import("~/lib/server/bucket-lists");
    const { keyGrants, ...item } = data;
    return updateBucketListItem(context.user.id, item, keyGrants);
  });

export const moveBucketListItemFn = createServerFn({ method: "POST" })
  .middleware([authMiddleware])
  .validator(MoveBucketListItemSchema)
  .handler(async ({ data, context }) => {
    const { moveBucketListItem } = await import("~/lib/server/bucket-lists");
    const userId = context.user.id;
    const { id, newOrder } = data;

    return moveBucketListItem(userId, id, newOrder);
  });

export const getSharedBucketListsFn = createServerFn({ method: "GET" })
  .middleware([requireClerkCoachAuth])
  .validator(GetSharedBucketListSchema)
  .handler(async ({ data, context }) => {
    const { getPublicUrl } = await import("~/lib/server/s3");
    const { getSharedBucketLists } = await import("~/lib/server/bucket-lists");
    const coachId = context.coach.id;

    const rows = await getSharedBucketLists(data.userId, coachId);

    return rows.map((row) => {
      const item = row.BucketListItems;
      const keys = item.images ?? [];
      return {
        ...item,
        imageUrls: keys.map(getPublicUrl),
        imageKeys: keys,
      };
    });
  });
